import { api } from "./api";

/* In-memory micro-cache for PUBLIC customer data only (restaurant list / detail / menu).
   Never used for orders, auth, wallet, checkout, nearby/GPS or any authenticated endpoint.
   Nothing is persisted: a full reload starts empty. Errors are never cached as data. */
const FRESH_MS = 30_000;          // served straight from memory
const STALE_MS = 120_000;         // served immediately, revalidated in the background
const store = new Map();          // key -> { data, etag, ts, gen }
const inflight = new Map();       // key -> { promise, gen }
const gens = new Map();           // key -> invalidation generation

const genOf = (key) => gens.get(key) || 0;

const keyOf = (url, params) => {
  const p = params || {};
  const qs = Object.keys(p).filter((k) => p[k] !== undefined && p[k] !== null).sort()
    .map((k) => `${k}=${String(p[k])}`).join("&");
  return qs ? `${url}?${qs}` : url;
};

function revalidate(key, url, params, { skipInflight = false } = {}) {
  const gen = genOf(key);
  const current = inflight.get(key);
  // Reuse an open request ONLY if it started after the last invalidation for this key.
  if (current && !skipInflight && current.gen === gen) return current.promise;
  const prev = store.get(key);
  const promise = (async () => {
    const res = await api.get(url, {
      params,
      headers: prev?.etag ? { "If-None-Match": prev.etag } : undefined,
      validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
    });
    const stale = gen !== genOf(key);                  // invalidated while this request was open
    if (res.status === 304 && prev) {
      if (!stale) store.set(key, { ...prev, ts: Date.now() });   // only a 304 refreshes freshness
      return prev.data;
    }
    const etag = res.headers?.etag || res.headers?.ETag;
    if (!stale) store.set(key, { data: res.data, etag, ts: Date.now() });
    return res.data;
  })().finally(() => { if (inflight.get(key)?.promise === promise) inflight.delete(key); });
  inflight.set(key, { promise, gen });
  return promise;
}

/** Cached GET with stale-while-revalidate + in-flight dedup.
 *  onUpdate(data) is called when a background revalidation returns changed data. */
export async function getPublic(url, { params, force = false, onUpdate } = {}) {
  const key = keyOf(url, params);
  const entry = store.get(key);
  const age = entry ? Date.now() - entry.ts : Infinity;
  if (!force && entry && age < FRESH_MS) return entry.data;
  if (!force && entry && age < STALE_MS) {
    revalidate(key, url, params)
      .then((d) => { if (onUpdate && d !== entry.data) onUpdate(d); })
      .catch(() => {});                                  // keep the valid payload, never cache the error
    return entry.data;
  }
  // A forced read must never be satisfied by a request that started BEFORE the change.
  return revalidate(key, url, params, { skipInflight: force });
}

/** Drop cached entries for an exact key (including its query variants) and void any request
 *  already in flight for them, so the next read always goes to the network. */
export function invalidatePublic(path) {
  for (const k of [...store.keys(), ...inflight.keys()]) {
    if (k === path || k.startsWith(`${path}?`)) {
      store.delete(k);
      gens.set(k, genOf(k) + 1);
    }
  }
  gens.set(path, genOf(path) + 1);
}

/** Drop only the entries that actually belong to ONE restaurant (matched on the cached payload),
 *  so an event for restaurant A never discards restaurant B's menu. */
export function invalidateRestaurant(restaurantId) {
  if (!restaurantId) return;
  for (const [k, v] of [...store.entries()]) {
    const r = v.data?.restaurant || v.data;
    if (r && !Array.isArray(r) && r.id === restaurantId) invalidatePublic(k.split("?")[0]);
  }
}
